// The always-editable clinical document editor — Phase 1 of
// docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md (§5.2 C11).
//
// Replaces the preview + raw-markdown-textarea toggle. The document is shown
// formatted and edited in place; fill-in fields are clickable chips; a 📅
// button sits beside dates and empty date labels; pre-filled lines carry an ⓘ
// naming their source; "Insert" (or "/" at the start of a word) adds values
// from structured data at the cursor.
//
// The value in and out is the same line-oriented markdown the rest of the app
// stores and backend/utils/docxGenerator.js reads — see documentModel.ts for
// why that conversion is custom rather than a markdown library.
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type ReactNode } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import * as Popover from '@radix-ui/react-popover';
import { Bold, Italic, Plus, Redo2, Undo2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { cn } from '@/lib/cn';
import { DateFieldPicker } from './DateFieldPicker';
import { DocAssistExtension, docAssistKey, type DateRequest, type DocAssistState, type HeadingSection, type SourceMarker } from './docAssist';
import { docToMarkdown, markdownToDoc, type DocJSON } from './documentModel';
import { detectSections } from './documentSections';
import { SmartFieldExtension } from './SmartFieldNode';
import { unfilledFieldLabels } from './smartFields';

export interface InsertableValue {
  key: string;
  label: string;
  /** Inserted at the cursor. May contain several lines and fill-in fields. */
  value: string;
}

export interface DocumentEditorProps {
  value: string;
  onChange: (markdown: string) => void;
  /** Values offered by the Insert menu ("/"), e.g. today's date, vitals, allergies. */
  insertables?: InsertableValue[];
  /** ⓘ markers for lines pre-filled from captured data. */
  sources?: SourceMarker[];
  /** C6 — renders a "↻ Regenerate this section" button beside every detected
   * heading line; omit to keep a caller (or a read-only view) without it. */
  onRegenerateSection?: (section: HeadingSection) => void;
  readOnly?: boolean;
  ariaLabel?: string;
  className?: string;
}

/** Imperative API for a caller sitting outside the editor's own toolbar — the
 * Consultation redesign's "Next field →" commit-bar control (2026-09-17)
 * needs to jump the doctor to the next fill-in field regardless of which tab
 * currently has focus. */
export interface DocumentEditorHandle {
  /** Moves the selection to the next `smartField` chip after the cursor,
   * wrapping to the first one in the document if none follows. Returns false
   * (no-op) when the document has no fill-in fields left, or is read-only. */
  focusNextField: () => boolean;
  /** v3 conversational layout — scrolls a detected section's heading into
   * view (the change ribbon / transcript's "↧ <Section>" jump chips), by
   * heading `key` (documentSections.ts). A view-only scroll, not a
   * selection change, and DOM-based rather than ProseMirror position math —
   * paragraphs render in document order, one per markdown line, so the
   * heading's line index is also its position among rendered `<p>`s.
   * Returns false when the section isn't currently in the document. */
  scrollToSection: (key: string) => boolean;
}

const FIELD_INSERTS: InsertableValue[] = [
  { key: 'field-date', label: 'Date to fill in', value: '{{date|Date}}' },
  { key: 'field-text', label: 'Text to fill in', value: '{{text|Details}}' },
];

function ToolbarButton({ label, active, disabled, onClick, children }: {
  label: string; active?: boolean; disabled?: boolean; onClick: () => void; children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // mousedown keeps the editor's selection; a click would blur it first.
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded text-ink-muted hover:bg-muted disabled:opacity-40',
        active && 'bg-teal-light text-teal'
      )}
    >
      {children}
    </button>
  );
}

const DocumentEditor = forwardRef<DocumentEditorHandle, DocumentEditorProps>(function DocumentEditor({
  value, onChange, insertables = [], sources = [], onRegenerateSection, readOnly = false, ariaLabel = 'Document', className,
}, ref) {
  const [dateRequest, setDateRequest] = useState<DateRequest | null>(null);
  const [insertOpen, setInsertOpen] = useState(false);

  // Refs read by editor callbacks created once, so they always see current props.
  const onChangeRef = useRef(onChange);
  const insertablesRef = useRef(insertables);
  const lastEmitted = useRef(value);
  const headingLines = useMemo(() => new Map(detectSections(value).map((s) => [s.startLine, { key: s.key, heading: s.heading }])), [value]);
  const assistState = useRef<DocAssistState>({ onDateRequest: setDateRequest, sources, editable: !readOnly, headingLines, onRegenerateSection });
  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);
  useEffect(() => { insertablesRef.current = insertables; }, [insertables]);

  const editor = useEditor({
    extensions: [
      // Only what the stored format can represent: paragraphs (one per line),
      // bold, italic, line breaks and undo. Headings, lists, code, links and
      // underline would be lost on save, so they're off.
      StarterKit.configure({
        heading: false, bulletList: false, orderedList: false, listItem: false, listKeymap: false,
        blockquote: false, codeBlock: false, code: false, horizontalRule: false, strike: false,
        link: false, underline: false,
      }),
      SmartFieldExtension,
      DocAssistExtension.configure({ getState: () => assistState.current }),
    ],
    content: markdownToDoc(value),
    editable: !readOnly,
    editorProps: {
      attributes: {
        class: 'min-h-[220px] whitespace-pre-wrap px-3 py-2 text-[13px] leading-relaxed text-ink outline-none [&_p]:min-h-[1.4em]',
        'aria-label': ariaLabel,
        'aria-multiline': 'true',
        role: 'textbox',
      },
      handleKeyDown: (view, event) => {
        // "/" at the start of a line or after a space opens the Insert menu;
        // anywhere else (e.g. "1/2", "05/09") it's just a character.
        if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return false;
        const { $from, empty } = view.state.selection;
        if (!empty) return false;
        const before = $from.parent.textBetween(Math.max(0, $from.parentOffset - 1), $from.parentOffset, undefined, '￼');
        if (before !== '' && !/\s/.test(before)) return false;
        setInsertOpen(true);
        return true;
      },
    },
    onUpdate: ({ editor: current }) => {
      const markdown = docToMarkdown(current.getJSON() as DocJSON);
      lastEmitted.current = markdown;
      onChangeRef.current(markdown);
    },
  });

  // A value set from outside (a new AI draft, a rebuilt sheet) replaces the
  // content; the editor's own edits don't round-trip back through here.
  useEffect(() => {
    if (!editor || value === lastEmitted.current) return;
    lastEmitted.current = value;
    editor.commands.setContent(markdownToDoc(value), { emitUpdate: false });
  }, [editor, value]);

  // Sources, headings and editability are read by the decoration plugin;
  // nudge it to rebuild. A `value` change already goes through
  // editor.commands.setContent above, which triggers the plugin's own
  // tr.docChanged rebuild — the explicit meta dispatch here only covers the
  // cases where the doc itself didn't change (sources/readOnly/heading map).
  useEffect(() => {
    assistState.current = { onDateRequest: setDateRequest, sources, editable: !readOnly, headingLines, onRegenerateSection };
    if (!editor) return;
    editor.setEditable(!readOnly);
    editor.view.dispatch(editor.state.tr.setMeta(docAssistKey, true));
  }, [editor, sources, readOnly, headingLines, onRegenerateSection]);

  const unfilled = useMemo(() => unfilledFieldLabels(value), [value]);

  useImperativeHandle(ref, () => ({
    focusNextField: () => {
      if (!editor || readOnly) return false;
      const positions: number[] = [];
      editor.state.doc.descendants((node, pos) => {
        if (node.type.name === 'smartField') positions.push(pos);
      });
      if (!positions.length) return false;
      const { to } = editor.state.selection;
      const next = positions.find((pos) => pos >= to) ?? positions[0]!;
      editor.chain().focus().setNodeSelection(next).scrollIntoView().run();
      return true;
    },
    scrollToSection: (key) => {
      if (!editor) return false;
      const entry = [...headingLines.entries()].find(([, section]) => section.key === key);
      if (!entry) return false;
      const [lineIndex] = entry;
      // Every top-level node is a paragraph (StarterKit's other block types
      // are disabled — see the extensions list above), one per markdown
      // line, always in document order, so the heading's line index is also
      // its index among the editor's own rendered `<p>` children.
      const target = editor.view.dom.children[lineIndex] as HTMLElement | undefined;
      if (!target) return false;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return true;
    },
  }), [editor, readOnly, headingLines]);

  const insertValue = (text: string) => {
    if (!editor) return;
    const paragraphs = markdownToDoc(text).content;
    const content = paragraphs.length === 1 ? paragraphs[0]!.content ?? [] : paragraphs;
    editor.chain().focus().insertContent(content).run();
  };

  const pickDate = (text: string) => {
    if (!editor || !dateRequest) return;
    const { from, to } = dateRequest;
    if (from === to) {
      const previous = editor.state.doc.textBetween(Math.max(0, from - 1), from);
      editor.chain().focus().insertContentAt(from, previous && !/\s/.test(previous) ? ` ${text}` : text).run();
    } else {
      editor.chain().focus().insertContentAt({ from, to }, text).run();
    }
    setDateRequest(null);
  };

  const dateAnchor = useMemo(
    () => ({ current: { getBoundingClientRect: () => dateRequest?.anchor.getBoundingClientRect() ?? new DOMRect() } }),
    [dateRequest]
  );

  return (
    <div className={cn('rounded-[var(--radius)] border border-border bg-surface', className)}>
      {!readOnly && (
        <div className="flex flex-wrap items-center gap-1 border-b border-border px-2 py-1">
          <ToolbarButton label="Bold" active={editor?.isActive('bold')} onClick={() => editor?.chain().focus().toggleBold().run()}>
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton label="Italic" active={editor?.isActive('italic')} onClick={() => editor?.chain().focus().toggleItalic().run()}>
            <Italic className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton label="Undo" disabled={!editor?.can().undo()} onClick={() => editor?.chain().focus().undo().run()}>
            <Undo2 className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton label="Redo" disabled={!editor?.can().redo()} onClick={() => editor?.chain().focus().redo().run()}>
            <Redo2 className="h-4 w-4" />
          </ToolbarButton>

          <DropdownMenu open={insertOpen} onOpenChange={setInsertOpen}>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="ghost" className="ml-1">
                <Plus className="h-3.5 w-3.5" /> Insert <span className="text-ink-muted">/</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" onCloseAutoFocus={(e) => { e.preventDefault(); editor?.commands.focus(); }}>
              {insertables.length > 0 && <DropdownMenuLabel>From this visit</DropdownMenuLabel>}
              {insertables.map((item) => (
                <DropdownMenuItem key={item.key} onSelect={() => insertValue(item.value)}>{item.label}</DropdownMenuItem>
              ))}
              {insertables.length > 0 && <DropdownMenuSeparator />}
              {FIELD_INSERTS.map((item) => (
                <DropdownMenuItem key={item.key} onSelect={() => insertValue(item.value)}>{item.label}</DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => insertValue('/')}>Type a “/” character</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <div className="ml-auto">
            {unfilled.length > 0 && (
              <Badge variant="warn" title={unfilled.join(', ')}>
                {unfilled.length} field{unfilled.length === 1 ? '' : 's'} to fill
              </Badge>
            )}
          </div>
        </div>
      )}

      <EditorContent editor={editor} />

      <Popover.Root open={!!dateRequest} onOpenChange={(open) => { if (!open) setDateRequest(null); }}>
        <Popover.Anchor virtualRef={dateAnchor} />
        <Popover.Portal>
          <Popover.Content
            side="bottom"
            align="start"
            sideOffset={6}
            className="z-50 rounded-md border border-border bg-popover p-3 text-popover-foreground shadow-md"
          >
            {dateRequest && <DateFieldPicker current={dateRequest.current} label={dateRequest.current ? 'Change date' : 'Insert date'} onPick={pickDate} />}
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </div>
  );
});

export default DocumentEditor;
